import { z } from 'zod';
import { currencyCode, jsonObject, minorUnits, timestamp, uuid } from './common.js';
import { listQueryInput, paginated } from './list.js';
import { userTier } from './user.js';
import { invoiceSchema, promoAppliesTo, promoCodeSchema, promoKind, subscriptionSchema, subscriptionStatus, } from './credit.js';
/**
 * Billing op DTOs (F19/F20/F24/F26/F45/F46/F56/F57/F70). Entities
 * (`credit_ledger`/`subscriptions`/`invoices`/`promo_codes`/`credit_packs`/
 * `action_credit_costs`) live in `./credit.ts`; this file is the op-DTO
 * sibling, mirroring the `document.ts` + `document.dto.ts` split — checkout,
 * subscription management, invoice reads, the Paddle webhook contract +
 * idempotency envelope, the F45/F46 checkout-return client state, and the
 * admin-only promo-catalog CRUD + billing dashboard read-model (F55/F70).
 */
// --- F24/F57 checkout (`billing.checkout.start`) -----------------------------
export const checkoutKind = z.enum(['pro_subscription', 'credit_pack']);
/**
 * `billing.checkout.start` — logged-in only (F34 AC: logged-out PRO CTA
 * routes to `/login` FIRST). `promoCode` is the F56 "Have a code?" inline
 * field; validity is re-checked server-side in the SAME transaction that
 * creates the Paddle checkout session, never trusted from a prior
 * `promo.redeem` call alone (race-safety, F56 AC).
 */
export const checkoutStartInput = z
    .object({
    kind: checkoutKind,
    /** Required when `kind='credit_pack'`; ignored for `pro_subscription`. */
    creditPackId: uuid.optional(),
    promoCode: z.string().trim().max(64, 'errors:promo.code.tooLong').optional(),
    /** Where Paddle's checkout overlay returns the browser to; the client appends `?checkout=success|error` (F45/F46). */
    returnUrl: z.string().url('errors:billing.checkout.returnUrl.invalid'),
})
    .refine((v) => v.kind !== 'credit_pack' || v.creditPackId !== undefined, {
    message: 'errors:billing.checkout.creditPackRequired',
    path: ['creditPackId'],
});
/**
 * `billing.checkout.start` result. `checkoutUrl` is the real Paddle-hosted
 * overlay URL in production; in `DEV_MODE` with no `PADDLE_API_KEY`,
 * `@xdocs/core` returns a deterministic `/dev/checkout-stub?...` URL instead
 * (E8 §11 dev-mode Paddle stub) so the F45/F46 return-state UI is
 * exercisable without live Paddle creds — same response shape either way.
 * `checkoutId` correlates this attempt with the eventual webhook for the
 * F45 "confirming" phase, but the WEBHOOK stays the sole truth source (F24
 * AC: never trust the return URL/checkout id alone to grant anything).
 */
export const checkoutStartResult = z.object({
    checkoutUrl: z.string().url(),
    checkoutId: z.string().min(1),
});
// --- F20/F24 subscription management -----------------------------------------
/** `billing.subscription.get` — no payload; null when the caller has never subscribed. */
export const subscriptionGetResult = subscriptionSchema.nullable();
/**
 * `billing.subscription.cancel` — default `atPeriodEnd: true` keeps PRO
 * active until `currentPeriodEnd` (F20/F24 AC: "canceling keeps PRO until
 * period end"); `atPeriodEnd: false` is the rare immediate-cancel path
 * (support-assisted). Local `subscriptions` row is a projection updated by
 * the Paddle webhook once it lands — this op calls the Paddle API and
 * optimistically marks `cancelAt`, matching F24's "Confirming…" gap pattern.
 */
export const subscriptionCancelInput = z.object({
    atPeriodEnd: z.boolean().default(true),
});
export const subscriptionCancelResult = subscriptionSchema;
/**
 * Push on `user:{userId}` (§5.5) — fired whenever the Paddle webhook (or an
 * admin `admin.users.update` tier change) flips `users.tier`. Sibling to
 * `creditsBalanceChangedPush` (`./credit.ts`) for the F47 pill / F45
 * confirmed-state / F20 plan-card live update.
 */
export const billingTierChangedPush = z.object({
    type: z.literal('billing.tierChanged'),
    tier: userTier,
});
// --- F26 invoices -------------------------------------------------------------
export const invoiceSortColumns = ['issuedAt'];
export const invoiceListQuery = listQueryInput(invoiceSortColumns, {
    defaultSort: 'issuedAt',
});
export const invoiceListResult = paginated(invoiceSchema);
// --- F45/F46 checkout-return client state ------------------------------------
//
// NOT a WS op — the client parses Paddle's `?checkout=success|error` redirect
// query param into this shape locally, then resolves `phase` by reading
// `billing.subscription.get`/`credits.balance` (whichever `kind` was
// purchased) and/or the live `billingTierChangedPush`/
// `creditsBalanceChangedPush` pushes those writes emit.
/** F46's three distinct, non-collapsible error cases. */
export const checkoutErrorReason = z.enum(['cancelled', 'payment_declined', 'past_due']);
/**
 * F45 AC: "success ≠ confirmed — never show 'upgraded' before the webhook
 * truth arrives." `confirming` = checkout returned `success` but the webhook
 * hasn't landed yet; `confirmed` = the local projection now reflects it;
 * `error` = one of `checkoutErrorReason`.
 */
export const checkoutReturnPhase = z.enum(['confirming', 'confirmed', 'error']);
export const checkoutReturnState = z.object({
    kind: checkoutKind,
    phase: checkoutReturnPhase,
    errorReason: checkoutErrorReason.optional(), // set when phase='error'
    /** F45 confirmed-pack naming: `Bought "Starter pack" — +500 credits`. */
    creditPackName: z.string().nullable().optional(),
    creditsGranted: z.number().int().positive().optional(),
});
// --- Paddle webhook contract (`POST /webhooks/paddle`, F24/F57) -------------
//
// Raw Paddle Billing wire shape — snake_case keys are Paddle's OWN contract
// verbatim, deliberately NOT remapped to this codebase's camelCase
// convention (common.ts header): these fields are parsed straight off
// Paddle's JSON, never round-tripped through our own client. Signature
// verification (Paddle SDK `webhooks.unmarshal`/`isSignatureValid` against
// `PADDLE_WEBHOOK_SECRET`) happens in `@xdocs/core` BEFORE this schema ever
// sees the body — out of this package's scope, which only pins the payload
// SHAPE once it's trusted.
export const paddleEventType = z.enum([
    'subscription.created',
    'subscription.updated',
    'subscription.canceled',
    'transaction.completed',
]);
/**
 * `custom_data` set at `billing.checkout.start` time — the ONLY reliable
 * link from a Paddle event back to `users.id` (Paddle has no native concept
 * of our user id). `@xdocs/core`'s checkout-session builder MUST always set
 * `custom_data.user_id`; a webhook whose `custom_data` is null/missing this
 * field cannot be applied and is dead-lettered (F70 AC "Paddle reconciliation").
 */
const paddleCustomData = z
    .object({ user_id: uuid })
    .nullable()
    .catch(null);
export const paddleSubscriptionEventData = z.object({
    id: z.string().min(1), // sub_...
    status: subscriptionStatus,
    customer_id: z.string().min(1),
    current_billing_period: z
        .object({ starts_at: z.string(), ends_at: z.string() })
        .nullable(),
    custom_data: paddleCustomData,
});
export const paddleTransactionEventData = z.object({
    id: z.string().min(1), // txn_...
    subscription_id: z.string().nullable(),
    customer_id: z.string().min(1),
    status: z.literal('completed'),
    currency_code: currencyCode,
    details: z.object({
        totals: z.object({ total: z.string(), currency_code: currencyCode }),
    }),
    invoice_id: z.string().nullable(),
    custom_data: paddleCustomData,
});
/**
 * The top-level webhook envelope. `event_id` is THE idempotency key: every
 * handler upserts a `paddleWebhookEvents` row (`@xdocs/db`, additive) keyed
 * on it inside the SAME transaction as the domain write, so a re-delivered
 * event (Paddle retries on any non-2xx response) is a guaranteed no-op on
 * replay — the same discipline `credit_ledger`'s `(reason, ref_id)`
 * partial-unique gives the events that ARE ledger writes (`transaction.completed`
 * → `reason='purchase', ref_id=data.id`), extended to cover
 * `subscription.*` events that are upserts, not ledger rows.
 */
export const paddleWebhookEnvelope = z.object({
    event_id: z.string().min(1),
    event_type: paddleEventType,
    occurred_at: z.string(),
    notification_id: z.string().min(1),
    data: z.unknown(), // discriminated by event_type — parse via parsePaddleEventData
});
/** Parses `data` against the shape matching `eventType`. Throws (ZodError) on a malformed payload — the webhook handler treats that as non-recoverable and dead-letters rather than retrying forever. */
export function parsePaddleEventData(eventType, data) {
    return eventType === 'transaction.completed'
        ? paddleTransactionEventData.parse(data)
        : paddleSubscriptionEventData.parse(data);
}
/** Persisted idempotency-envelope row shape (`packages/db/src/schema/money.ts` `paddleWebhookEvents`, additive). */
export const paddleWebhookEventSchema = z.object({
    id: uuid,
    paddleEventId: z.string().min(1),
    eventType: paddleEventType,
    payloadJsonb: jsonObject,
    processedAt: timestamp.nullable(),
    createdAt: timestamp,
});
/**
 * `POST /webhooks/paddle`'s REST response — a plain 2xx/4xx per E17 §5.1
 * (this endpoint is a REST escape hatch, not a WS op), but the ack body is
 * still a pinned shape for the dev-only webhook simulator
 * (`checkoutSimulateWebhookInput` below) to assert against in tests.
 */
export const paddleWebhookAck = z.object({ received: z.literal(true), eventId: z.string() });
/**
 * Dev-only webhook simulator input (E8 §11: `DEV_MODE=true` + no
 * `PADDLE_API_KEY` → `@xdocs/core` exposes a signed, deterministic webhook
 * simulator endpoint so the F24/F45/F46/F57 flows are testable without live
 * Paddle creds). `apps/server` fail-closed-gates this: the route 404s unless
 * `NODE_ENV !== 'production'` AND `DEV_MODE === true` — never reachable in a
 * prod deploy regardless of env-var misconfiguration (E8 "MUST default to
 * production-safe and fail-closed").
 */
export const checkoutSimulateWebhookInput = z.object({
    checkoutId: z.string().min(1),
    eventType: paddleEventType,
    /** Overrides for the simulated `data` block; merged over deterministic defaults keyed by `checkoutId`. */
    dataOverrides: jsonObject.optional(),
});
// --- F55 admin promo-catalog CRUD --------------------------------------------
export const adminPromoCreateInput = z.object({
    code: z.string().trim().min(1, 'errors:promo.code.required').max(64, 'errors:promo.code.tooLong'),
    kind: promoKind,
    valueNumeric: z.number(),
    currency: currencyCode.optional(), // fixed_off only
    maxRedemptions: z.number().int().positive().nullable().optional(),
    perUserLimit: z.number().int().positive().default(1),
    validFrom: timestamp.nullable().optional(),
    validUntil: timestamp.nullable().optional(),
    appliesTo: promoAppliesTo,
    appliesToRefId: uuid.optional(),
});
export const adminPromoCreateResult = promoCodeSchema;
/** `admin.promo.update` — every field optional except the id being edited. */
export const adminPromoUpdateInput = adminPromoCreateInput.partial().extend({ promoCodeId: uuid });
export const adminPromoUpdateResult = promoCodeSchema;
/** `admin.promo.deactivate` — F55 AC: a code already redeemed can't be hard-deleted, only deactivated (`isActive=false`); no `admin.promo.delete` op exists. */
export const adminPromoDeactivateInput = z.object({ promoCodeId: uuid });
export const adminPromoDeactivateResult = promoCodeSchema;
export const adminPromoSortColumns = ['createdAt', 'code', 'redemptionCount', 'validUntil'];
export const adminPromoListQuery = listQueryInput(adminPromoSortColumns, {
    defaultSort: 'createdAt',
});
export const adminPromoListResult = paginated(promoCodeSchema);
// --- F70 admin Billing dashboard (admin-only, cached PG read-model, E1) -----
/**
 * `admin.billing.overview` — aggregated over `subscriptions`/`invoices`/
 * `credit_ledger`/`promo_redemptions` via a cached read-model (E1), NOT a
 * warehouse (§15). `recentMismatches` is a bounded (≤20) recent-first slice
 * for the dashboard widget, not a paginated list — F70's full drill-down
 * lists (invoices/refunds/promo usage/credit purchases) reuse
 * `invoiceListQuery`/`adminPromoListQuery`/`creditLedgerListQuery` directly
 * rather than a duplicate op.
 */
export const paddleReconciliationRow = z.object({
    userId: uuid,
    localSubscriptionId: uuid.nullable(),
    paddleSubscriptionId: z.string().nullable(),
    localStatus: subscriptionStatus.nullable(),
    paddleStatus: subscriptionStatus.nullable(),
    detectedAt: timestamp,
});
export const adminBillingOverviewResult = z.object({
    mrrMinor: minorUnits,
    currency: currencyCode,
    activeSubscriptions: z.number().int().nonnegative(),
    churnRatePercent: z.number(),
    recentMismatches: z.array(paddleReconciliationRow).max(20),
    generatedAt: timestamp,
});
